import { useState, useEffect, useRef } from "react"
import { BarChart2, TrendingUp, TrendingDown, Download, Calendar, Award, AlertTriangle } from "lucide-react"
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from "recharts"
import api from "../services/api"
import { formatCurrency, formatPct } from "../utils/formatters"
import { PageLoader } from "../components/common/LoadingSpinner"

const PERIODS = [{ label: "7D", value: "7d" }, { label: "30D", value: "30d" }, { label: "90D", value: "90d" }, { label: "1Y", value: "1y" }]
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

// Mock data to show meaningful UI even without backend
function getMock(period) {
  const points = period === "7d" ? 7 : period === "30d" ? 30 : period === "90d" ? 90 : 365
  let equity = 10000
  const curve = Array.from({ length: points }, (_, i) => {
    equity *= (1 + (Math.random() - 0.42) * 0.03)
    return { date: new Date(Date.now() - (points - i) * 86400000).toISOString().slice(0,10), equity: Math.round(equity) }
  })
  return {
    total_return_pct: ((equity - 10000) / 10000) * 100,
    sharpe_ratio: 1.45 + Math.random() * 0.5,
    max_drawdown_pct: 8.3 + Math.random() * 5,
    win_rate_pct: 58 + Math.random() * 15,
    total_trades: Math.round(points * 2.5),
    profit_factor: 1.4 + Math.random() * 0.6,
    avg_win_pct: 2.8,
    avg_loss_pct: -1.4,
    equity_curve: curve,
  }
}

const MONTHLY_RETURNS = { 0: 3.2, 1: -1.8, 2: 5.4, 3: 2.1, 4: -0.9, 5: 4.7, 6: 1.3, 7: -2.4, 8: 6.1, 9: 3.8, 10: null, 11: null }
const BY_STRATEGY = [
  { name: "EMA Cross", pnl: 1842, trades: 48, win_rate: 62 },
  { name: "RSI MR", pnl: 743, trades: 92, win_rate: 71 },
  { name: "Breakout", pnl: -234, trades: 21, win_rate: 47 },
  { name: "Grid", pnl: 1210, trades: 134, win_rate: 83 },
  { name: "Scalping", pnl: 412, trades: 280, win_rate: 59 },
]
const BEST_TRADES = [
  { symbol: "BTC/USDT", side: "long", pnl_pct: 14.2, pnl_usd: 1420, date: "2024-09-12" },
  { symbol: "ETH/USDT", side: "long", pnl_pct: 11.8, pnl_usd: 980, date: "2024-08-22" },
  { symbol: "SOL/USDT", side: "long", pnl_pct: 9.4, pnl_usd: 740, date: "2024-10-05" },
]
const WORST_TRADES = [
  { symbol: "BTC/USDT", side: "short", pnl_pct: -6.8, pnl_usd: -680, date: "2024-10-01" },
  { symbol: "XRP/USDT", side: "long", pnl_pct: -5.1, pnl_usd: -420, date: "2024-09-18" },
  { symbol: "BNB/USDT", side: "long", pnl_pct: -4.4, pnl_usd: -360, date: "2024-08-30" },
]

function downloadCSV(data) {
  const rows = [["Date","Equity"], ...(data.equity_curve || []).map(d => [d.date, d.equity])]
  const csv = rows.map(r => r.join(",")).join("\n")
  const blob = new Blob([csv], { type: "text/csv" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url; a.download = "equity_curve.csv"; a.click()
}

export default function Reports() {
  const [period, setPeriod] = useState("30d")
  const [data, setData] = useState(null)
  const [byStrategy, setByStrategy] = useState([])
  const [loading, setLoading] = useState(true)
  const mockCache = useRef({})

  async function load(p) {
    setLoading(true)
    try {
      const [perf, strat] = await Promise.all([api.get(`/reports/performance?period=${p}`), api.get("/reports/trades-by-strategy")])
      setData(perf.data)
      setByStrategy(strat.data?.length ? strat.data : BY_STRATEGY)
    } catch {
      if (!mockCache.current[p]) mockCache.current[p] = getMock(p)
      setData(mockCache.current[p])
      setByStrategy(BY_STRATEGY)
    }
    finally { setLoading(false) }
  }
  useEffect(() => { load(period) }, [period])

  const totalReturn = data?.total_return_pct ?? 0
  const metrics = [
    ["Total Return", formatPct(totalReturn), totalReturn >= 0 ? "text-trading-green" : "text-trading-red"],
    ["Sharpe Ratio", data?.sharpe_ratio?.toFixed(2) ?? "—", "text-white"],
    ["Max Drawdown", formatPct(-(data?.max_drawdown_pct ?? 0)), "text-trading-red"],
    ["Win Rate", formatPct(data?.win_rate_pct), "text-white"],
    ["Total Trades", data?.total_trades ?? "—", "text-white"],
    ["Profit Factor", data?.profit_factor?.toFixed(2) ?? "—", (data?.profit_factor ?? 0) >= 1.5 ? "text-trading-green" : "text-trading-red"],
    ["Avg Win", formatPct(data?.avg_win_pct), "text-trading-green"],
    ["Avg Loss", formatPct(data?.avg_loss_pct), "text-trading-red"],
  ]

  // Use API data when available, fall back to static demo data
  const monthlyReturns = data?.monthly_returns ?? MONTHLY_RETURNS
  const bestTrades = data?.best_trades ?? BEST_TRADES
  const worstTrades = data?.worst_trades ?? WORST_TRADES

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Performance Reports</h2>
          <p className="text-sm text-gray-400 mt-1">Analytics across all strategies and bots</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {PERIODS.map(p => (
              <button key={p.value} onClick={() => setPeriod(p.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${period === p.value ? "bg-brand-500 text-white" : "text-gray-400 hover:text-white bg-surface-100"}`}>{p.label}</button>
            ))}
          </div>
          {data && <button onClick={() => downloadCSV(data)} className="btn-secondary flex items-center gap-2 text-xs"><Download size={13}/> CSV</button>}
        </div>
      </div>

      {loading ? <PageLoader/> : (
        <>
          <div className="grid grid-cols-4 gap-3">
            {metrics.map(([k,v,c]) => (
              <div key={k} className="card text-center py-4">
                <p className="text-xs text-gray-400 mb-1">{k}</p>
                <p className={`text-xl font-bold font-mono ${c}`}>{v}</p>
              </div>
            ))}
          </div>

          {data?.equity_curve?.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-white mb-4">Equity Curve</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.equity_curve}>
                  <defs>
                    <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a"/>
                  <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v?.slice(5)}/>
                  <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(1)}k`}/>
                  <Tooltip contentStyle={{ background: "#1a1d27", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }} formatter={v => [`$${v?.toLocaleString()}`, "Equity"]} labelStyle={{ color: "#9ca3af" }}/>
                  <Area type="monotone" dataKey="equity" stroke="#6366f1" strokeWidth={2} fill="url(#eqGrad)"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Monthly Returns Heatmap */}
            <div className="card">
              <h3 className="font-semibold text-white mb-4">Monthly Returns {new Date().getFullYear()}</h3>
              <div className="grid grid-cols-4 gap-2">
                {MONTHS.map((m, i) => {
                  const val = monthlyReturns[i]
                  const bg = val === null ? "bg-surface-100" : val > 0 ? "bg-trading-green/15 border-trading-green/25" : "bg-trading-red/15 border-trading-red/25"
                  return (
                    <div key={m} className={`${bg} border border-surface-200 rounded-xl p-3 text-center`}>
                      <p className="text-[10px] text-gray-500 mb-1">{m}</p>
                      <p className={`text-sm font-mono font-bold ${val === null ? "text-gray-600" : val > 0 ? "text-trading-green" : "text-trading-red"}`}>
                        {val === null ? "—" : `${val > 0 ? "+" : ""}${val}%`}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* By Strategy */}
            <div className="card">
              <h3 className="font-semibold text-white mb-4">P&L by Strategy</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={byStrategy} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" horizontal={false}/>
                  <XAxis type="number" tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`}/>
                  <YAxis type="category" dataKey="name" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} width={70}/>
                  <Tooltip contentStyle={{ background: "#1a1d27", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }} formatter={v => [`$${v}`, "P&L"]}/>
                  <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                    {byStrategy.map((entry, i) => <Cell key={i} fill={entry.pnl >= 0 ? "#00d395" : "#ff4d4f"}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {[["Best Trades", bestTrades, "text-trading-green", TrendingUp], ["Worst Trades", worstTrades, "text-trading-red", TrendingDown]].map(([title, trades, color, Icon]) => (
              <div key={title} className="card">
                <div className="flex items-center gap-2 mb-4">
                  <Icon size={16} className={color}/>
                  <h3 className="font-semibold text-white">{title}</h3>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-gray-500 text-xs border-b border-surface-200">
                    {["Symbol","Side","P&L%","P&L $","Date"].map(h => <th key={h} className="pb-2 font-medium text-left">{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-surface-200/50">
                    {trades.map((t, i) => (
                      <tr key={i} className="hover:bg-surface-100">
                        <td className="py-2 font-mono text-white text-xs">{t.symbol}</td>
                        <td className="py-2 text-xs"><span className={t.side === "long" ? "text-trading-green" : "text-trading-red"}>{t.side.toUpperCase()}</span></td>
                        <td className={`py-2 font-mono text-xs font-semibold ${color}`}>{t.pnl_pct > 0 ? "+" : ""}{t.pnl_pct}%</td>
                        <td className={`py-2 font-mono text-xs ${color}`}>{t.pnl_usd > 0 ? "+" : ""}${Math.abs(t.pnl_usd)}</td>
                        <td className="py-2 text-gray-500 text-xs">{t.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
